/**
 * WP-V2-LODZ — Lodziarnia „Pieskowo Lodziarnia".
 * Interaktywne wnętrze: lada, witryna smaków, stoliki, zakup loda za monety.
 * Wizualia: proceduralne gałki i witryna (bez emoji-obiektów).
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { balanceBus } from '../../core/balanceBus.js';
import {
  AnimatedDogAvatar,
  OutfitStore,
  preloadAnchors,
} from '../../avatar/index.js';
import { playFx } from '../../interact/index.js';
import {
  setupWorld,
  follow,
  WalkController,
} from '../../core/world/index.js';
import {
  ICECREAM_COLLIDERS,
  ICECREAM_WORLD_H,
  ICECREAM_WORLD_W,
  ICECREAM_TABLES,
  COUNTER,
  COUNTER_APPROACH,
  PLAYER_START,
  VENDOR_POS,
  ensureIcecreamTextures,
} from './objects.js';
import {
  ICE_CREAM_CONE_TEXTURE,
  ICE_CREAM_FLAVORS,
  ICE_CREAM_PRICE_ZL,
  addScoop,
  emptyOrderState,
  flavorById,
  openConfirm,
  closeConfirm,
  orderPriceZl,
  orderSummaryPl,
  priceLabelPl,
  selectFlavor,
  setOrderBusy,
  setVessel,
  type IceCreamFlavorId,
  type IceCreamOrderState,
  type IceCreamVessel,
} from './icecreamLogic.js';
import { buyIceCream, refreshIceCreamBalance } from './icecreamApi.js';
import { narrator } from '../../core/Narrator.js';
import { buildLickSequence } from './lickSequence.js';

export class IcecreamScene extends BaseScene {
  static readonly KEY = 'Icecream';

  private avatar: AnimatedDogAvatar | null = null;
  private walk: WalkController | null = null;
  private balance = 0;
  private order: IceCreamOrderState = emptyOrderState();
  private balanceText: Phaser.GameObjects.Text | null = null;
  private toast: Phaser.GameObjects.Text | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private flavorButtons: Phaser.GameObjects.Container[] = [];
  private selectedRing: Phaser.GameObjects.Ellipse | null = null;
  private buying = false;
  private heldCone: Phaser.GameObjects.Container | null = null;
  private heldScoops: Phaser.GameObjects.GameObject[] = [];
  private confirmUi: Phaser.GameObjects.Container | null = null;
  private confirmHitZones: Phaser.GameObjects.Zone[] = [];
  private summaryText: Phaser.GameObjects.Text | null = null;
  private sceneAlive = false;

  constructor() {
    super(IcecreamScene.KEY);
  }

  preload(): void {
    if (!this.textures.exists('bg_icecream')) {
      this.load.image('bg_icecream', '/assets/real/bg_icecream.png');
    }
    if (!this.textures.exists('bldg_icecream')) {
      this.load.image('bldg_icecream', '/assets/real/bldg_icecream.png');
    }
    for (const pose of ['idle', 'walkA', 'walkB', 'sit', 'jump', 'wave'] as const) {
      const key = `dog_maltese_${pose}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/maltese_${pose}.png`);
      }
    }
    if (!this.textures.exists('ph_dog')) {
      this.load.image('ph_dog', '/assets/placeholder/dog.png');
    }
    if (!this.textures.exists(ICE_CREAM_CONE_TEXTURE)) {
      this.load.image(
        ICE_CREAM_CONE_TEXTURE,
        '/assets/real/icecream_cone_empty.png',
      );
    }
    if (!this.textures.exists('icecream_cup_empty')) {
      this.load.image('icecream_cup_empty', '/assets/real/icecream_cup_empty.png');
    }
    for (const flavor of ICE_CREAM_FLAVORS) {
      if (!this.textures.exists(flavor.textureKey)) {
        this.load.image(
          flavor.textureKey,
          `/assets/real/${flavor.textureKey}.png`,
        );
      }
    }
    void preloadAnchors(this);
  }

  create(): void {
    this.sceneAlive = true;
    const cache = OutfitStore.load();
    this.balance = cache.balanceZl;
    this.order = emptyOrderState();

    setupWorld(this, {
      worldW: ICECREAM_WORLD_W,
      worldH: ICECREAM_WORLD_H,
      bg: 0xffe4ec,
    });

    ensureIcecreamTextures(this);
    this.drawInterior();
    this.spawnPlayer(cache.breed, cache.color);
    this.buildChrome();
    this.buildFlavorMenu();
    this.spawnCounterHotspot();
    this.spawnVendorHotspot();
    this.spawnTableHotspots();
    this.spawnBackButton();

    this.events.once('shutdown', () => this.teardown());
    void this.refreshBalance();
  }

  update(_t: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000);
    this.walk?.update(dt);
    const pos = this.walk?.position;
    this.avatar?.update(dt);
    if (pos && this.avatar) {
      this.avatar.container.setPosition(pos.x, pos.y);
      this.registry.set('companionFollow', { x: pos.x, y: pos.y });
    }
  }

  private drawInterior(): void {
    const hasIllustratedBg = this.textures.exists('bg_icecream');
    if (hasIllustratedBg) {
      this.add
        .image(
          ICECREAM_WORLD_W / 2,
          ICECREAM_WORLD_H / 2,
          'bg_icecream',
        )
        .setDisplaySize(ICECREAM_WORLD_W, ICECREAM_WORLD_H)
        .setDepth(-110);
    }
    // ściany / podłoga — styl Toca (pastel, bez wtopionych napisów)
    this.add
      .rectangle(
        ICECREAM_WORLD_W / 2,
        ICECREAM_WORLD_H / 2,
        ICECREAM_WORLD_W,
        ICECREAM_WORLD_H,
        0xfff0f5,
      )
      .setDepth(-100)
      .setAlpha(this.textures.exists('bg_icecream') ? 0.14 : 1);
    this.add
      .rectangle(ICECREAM_WORLD_W / 2, 620, ICECREAM_WORLD_W, 220, 0xf8e1e7)
      .setDepth(-90)
      .setAlpha(this.textures.exists('bg_icecream') ? 0.18 : 1);
    // pas dekoracyjny na ścianie
    this.add
      .rectangle(ICECREAM_WORLD_W / 2, 120, ICECREAM_WORLD_W, 80, 0xffc1d6, 0.55)
      .setDepth(-80);

    if (this.textures.exists('bldg_icecream')) {
      this.add
        .image(200, 140, 'bldg_icecream')
        .setDisplaySize(160, 160)
        .setAlpha(0.85)
        .setDepth(-70);
    }

    // witryna z lodami (szkło + proceduralna ramka)
    this.add
      .rectangle(COUNTER.x, 200, 680, 100, 0xe3f2fd, 0.85)
      .setStrokeStyle(4, 0xffffff)
      .setDepth(2)
      .setAlpha(hasIllustratedBg ? 0.08 : 1);
    this.add
      .image(COUNTER.x - 300, 200, 'icecream_showcase')
      .setDisplaySize(48, 48)
      .setDepth(3)
      .setAlpha(hasIllustratedBg ? 0.08 : 0.85);
    this.add
      .image(COUNTER.x + 300, 200, 'icecream_showcase')
      .setDisplaySize(48, 48)
      .setDepth(3)
      .setAlpha(hasIllustratedBg ? 0.08 : 0.85);

    // gałki w witrynie — proceduralne kulki (bez emoji)
    ICE_CREAM_FLAVORS.forEach((f, i) => {
      const x = 520 + i * 150;
      this.add
        .image(x, 198, f.textureKey)
        .setDisplaySize(56, 56)
        .setDepth(4)
        .setAlpha(hasIllustratedBg ? 0.62 : 1);
      this.add
        .text(x, 248, f.namePl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(4);
    });

    // lada
    this.add
      .rectangle(COUNTER.x, COUNTER.y, COUNTER.w, COUNTER.h, 0xffb7c5)
      .setStrokeStyle(4, 0xffffff)
      .setDepth(5)
      .setAlpha(hasIllustratedBg ? 0.08 : 1);
    this.add
      .rectangle(COUNTER.x, COUNTER.y - 28, COUNTER.w - 40, 18, 0xffffff, 0.55)
      .setDepth(6)
      .setAlpha(hasIllustratedBg ? 0.08 : 1);

    // stoliki (wizual — klikalne w spawnTableHotspots)
    for (const t of ICECREAM_TABLES) {
      this.add
        .ellipse(t.x, t.y, 140, 52, 0xffe0b2)
        .setStrokeStyle(3, 0xffffff)
        .setDepth(4);
      this.add.rectangle(t.x - 40, t.y + 28, 12, 36, 0xd7a86e).setDepth(3);
      this.add.rectangle(t.x + 40, t.y + 28, 12, 36, 0xd7a86e).setDepth(3);
    }

    // sprzedawczyni — prawdziwa tekstura psa + rożek (klikalna w spawnVendorHotspot)
    this.add
      .circle(VENDOR_POS.x, VENDOR_POS.y, 34, 0xfff8f0)
      .setStrokeStyle(3, 0xffb6c1)
      .setDepth(8);
    const vendorKey = this.textures.exists('dog_maltese_idle')
      ? 'dog_maltese_idle'
      : this.textures.exists('ph_dog')
        ? 'ph_dog'
        : null;
    if (vendorKey) {
      this.add
        .image(VENDOR_POS.x, VENDOR_POS.y - 4, vendorKey)
        .setDisplaySize(52, 52)
        .setDepth(9);
    } else {
      this.add.circle(VENDOR_POS.x, VENDOR_POS.y - 4, 22, 0xffe0b2).setDepth(9);
    }
    this.add
      .image(VENDOR_POS.x, VENDOR_POS.y - 42, ICE_CREAM_CONE_TEXTURE)
      .setDisplaySize(28, 28)
      .setDepth(10);
  }

  private spawnPlayer(breed: string, color: string): void {
    const cache = OutfitStore.load();
    this.avatar = new AnimatedDogAvatar({
      scene: this,
      x: PLAYER_START.x,
      y: PLAYER_START.y,
      breed: breed || cache.breed || 'maltese',
      color: color || cache.color || '#FFF5EE',
      slots: cache.slots,
      swimSlots: cache.swimSlots,
      depth: 100,
    });

    this.walk = new WalkController(this, this.avatar, {
      colliders: ICECREAM_COLLIDERS,
      bounds: {
        minX: 48,
        minY: 140,
        maxX: ICECREAM_WORLD_W - 48,
        maxY: ICECREAM_WORLD_H - 48,
      },
      enableInput: true,
    });

    follow(this, this.avatar.container, {
      lerp: 0.12,
      deadzone: { width: 100, height: 70 },
    });
  }

  private buildChrome(): void {
    this.add
      .text(ICECREAM_WORLD_W / 2, 36, 'Pieskowo Lodziarnia', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '34px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);

    this.add
      .text(ICECREAM_WORLD_W / 2, 74, 'Wybierz smak przy ladzie i kup loda!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);

    this.add
      .circle(70, 36, 22, 0xffd700)
      .setStrokeStyle(2, 0xffffff)
      .setScrollFactor(0)
      .setDepth(20_000);
    // Saldo tylko w globalnym HUD (lewy górny) — lokalny chip był nachodził na „Wróć".
    this.balanceText = null;
    this.summaryText = this.add
      .text(ICECREAM_WORLD_W / 2, ICECREAM_WORLD_H - 148, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        backgroundColor: '#ffffffdd',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_100);

    this.statusText = this.add
      .text(ICECREAM_WORLD_W / 2, 108, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6b5f7a',
        backgroundColor: '#ffffffcc',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000)
      .setVisible(false);

    this.toast = this.add
      .text(ICECREAM_WORLD_W / 2, 150, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#fff8f0',
        padding: { x: 14, y: 8 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_050)
      .setVisible(false);
  }

  private buildFlavorMenu(): void {
    // panel smaków (HUD) — nisko, ale nad paskiem akcji
    const baseY = ICECREAM_WORLD_H - 130;
    const startX = ICECREAM_WORLD_W / 2 - ((ICE_CREAM_FLAVORS.length - 1) * 150) / 2;

    ICE_CREAM_FLAVORS.forEach((f, i) => {
      const x = startX + i * 150;
      const bg = this.add
        .rectangle(0, 0, 132, 88, f.color, 0.92)
        .setStrokeStyle(3, 0xffffff);
      const scoop = this.add
        .image(0, -14, f.textureKey)
        .setDisplaySize(40, 40);
      const name = this.add
        .text(0, 20, f.namePl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      const c = this.add.container(x, baseY, [bg, scoop, name]);
      c.setSize(132, 88);
      c.setScrollFactor(0);
      c.setDepth(20_100);
      const hit = this.add
        .zone(x, baseY, 132, 88)
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(20_120)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => {
        narrator.sayText(`Lody ${f.namePl.toLowerCase()}`, { immediate: false });
        this.setStatus(`Lody: ${f.namePl}`);
      });
      hit.on('pointerdown', () => this.onFlavorPick(f.id));
      hit.setName(`hotspot-flavor-${f.id}`);
      this.flavorButtons.push(c);
    });

    this.selectedRing = this.add
      .ellipse(0, 0, 140, 96)
      .setStrokeStyle(4, 0xffd700)
      .setFillStyle(0xffffff, 0)
      .setScrollFactor(0)
      .setDepth(20_090)
      .setVisible(false);

    // Naczynie: rożek / kubeczek
    const vesselY = ICECREAM_WORLD_H - 48;
    for (const [v, label, ox] of [
      ['cone', 'Rożek', ICECREAM_WORLD_W / 2 - 200],
      ['cup', 'Kubeczek', ICECREAM_WORLD_W / 2 - 60],
    ] as const) {
      const b = UiKit.button(this, {
        x: ox,
        y: vesselY,
        label,
        width: 130,
        height: 52,
        fill: this.order.vessel === v ? 0xffd1e8 : 0xfff8f0,
        onClick: () => {
          this.order = setVessel(this.order, v as IceCreamVessel);
          this.refreshSummary();
          this.showToast(v === 'cone' ? 'Rożek wafelkowy!' : 'Kubeczek!');
        },
      });
      b.setScrollFactor(0);
      b.setDepth(20_110);
      this.add
        .zone(ox, vesselY, 130, MIN_TOUCH)
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(20_120)
        .setInteractive({ useHandCursor: true })
        .setName(`hotspot-vessel-${v}`)
        .on('pointerdown', () => {
          narrator.sayText(label, { immediate: true });
          this.order = setVessel(this.order, v as IceCreamVessel);
          this.refreshSummary();
          this.showToast(v === 'cone' ? 'Rożek wafelkowy!' : 'Kubeczek!');
        });
    }

    const buyBtn = UiKit.button(this, {
      x: ICECREAM_WORLD_W / 2 + 160,
      y: vesselY,
      label: 'Potwierdź',
      fill: 0xb8e6d0,
      width: 200,
      height: MIN_TOUCH,
      onClick: () => this.onConfirmIntent(),
    });
    buyBtn.setScrollFactor(0);
    buyBtn.setDepth(20_110);
    this.add
      .zone(ICECREAM_WORLD_W / 2 + 160, vesselY, 200, MIN_TOUCH)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_120)
      .setInteractive({ useHandCursor: true })
      .setName('hotspot-buy-icecream')
      .on('pointerdown', () => this.onConfirmIntent());
    this.refreshSummary();
  }

  private spawnCounterHotspot(): void {
    const hit = this.add
      .rectangle(COUNTER.x, COUNTER.y + 40, COUNTER.w - 80, 80, 0xffffff, 0.001)
      .setDepth(50)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerdown', () => {
      this.walk?.moveTo(COUNTER_APPROACH.x, COUNTER_APPROACH.y);
      this.showToast('Wybierz smak na dole ekranu!');
      playFx(this, 'sparkle', COUNTER_APPROACH.x, COUNTER.y);
      void this.avatar?.playAction('happy');
    });
    hit.setName('hotspot-counter');
  }

  /** Sprzedawczyni — powitanie + FX (była martwą dekoracją). */
  private spawnVendorHotspot(): void {
    const hit = this.add
      .circle(VENDOR_POS.x, VENDOR_POS.y, 44, 0xffffff, 0.001)
      .setDepth(50)
      .setInteractive({
        useHandCursor: true,
        hitArea: new Phaser.Geom.Circle(0, 0, 48),
        hitAreaCallback: Phaser.Geom.Circle.Contains,
      });
    hit.on('pointerdown', () => {
      this.walk?.moveTo(COUNTER_APPROACH.x, COUNTER_APPROACH.y);
      playFx(this, 'hearts', VENDOR_POS.x, VENDOR_POS.y - 30);
      this.showToast('Hau! Jakie lody dzisiaj? 🍦');
      void this.avatar?.playAction('wave');
    });
    hit.setName('hotspot-vendor');
  }

  /** Stoliki — podejście + siadanie. */
  private spawnTableHotspots(): void {
    for (const t of ICECREAM_TABLES) {
      const hit = this.add
        .ellipse(t.x, t.y, 150, 70, 0xffffff, 0.001)
        .setDepth(50)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        this.walk?.moveTo(t.approachX, t.approachY);
        playFx(this, 'hearts', t.x, t.y - 20);
        this.showToast('Siadamy przy stoliku!');
        void this.avatar?.playAction('sit');
      });
      hit.setName(`hotspot-${t.id}`);
    }
  }

  private spawnBackButton(): void {
    // Prawy górny róg — nie koliduje z HUD salda (lewy górny)
    const btn = UiKit.button(this, {
      x: this.gameW - 100,
      y: 48,
      label: '← Miasto',
      width: 160,
      height: MIN_TOUCH,
      fill: 0xb8e6d0,
      onClick: () => getSceneRouter().go('Town'),
    });
    btn.setScrollFactor(0);
    btn.setDepth(20_100);
    btn.setData('navTarget', 'Town');
    btn.setName('hotspot-back-Town');
  }

  private refreshSummary(): void {
    this.summaryText?.setText(orderSummaryPl(this.order));
  }

  private onFlavorPick(id: IceCreamFlavorId): void {
    const f = flavorById(id);
    if (!f) return;

    // Pierwsza gałka = wybór; kolejne kliknięcia tego samego / innego = dołóż (max 3)
    if (this.order.scoops.length === 0) {
      this.order = selectFlavor(this.order, id);
    } else if (this.order.scoops.length < 3) {
      this.order = addScoop(this.order, id);
    } else {
      this.showToast('Max 3 gałki — potwierdź zakup!');
    }

    const idx = ICE_CREAM_FLAVORS.findIndex((x) => x.id === id);
    const btn = this.flavorButtons[idx];
    if (btn && this.selectedRing) {
      this.selectedRing.setPosition(btn.x, btn.y);
      this.selectedRing.setVisible(true);
    }

    this.walk?.moveTo(COUNTER_APPROACH.x, COUNTER_APPROACH.y);
    narrator.sayText(f.namePl, { immediate: true });
    this.refreshSummary();
    this.setStatus(orderSummaryPl(this.order));
    playFx(this, 'sparkle', COUNTER_APPROACH.x, COUNTER.y - 20);
  }

  /** Otwórz dialog: na pewno kupić / dołóż gałkę. */
  private onConfirmIntent(): void {
    if (this.buying || this.order.busy) return;
    if (this.order.scoops.length === 0) {
      this.showToast('Najpierw wybierz smak loda!');
      return;
    }
    this.order = openConfirm(this.order);
    this.showConfirmDialog();
  }

  private showConfirmDialog(): void {
    this.hideConfirmDialog();
    const cx = this.gameW / 2;
    const cy = this.gameH / 2;
    const bg = this.add
      .rectangle(0, 0, 520, 280, 0xfff8f0, 0.98)
      .setStrokeStyle(4, 0xffb6c1);
    const title = this.add
      .text(0, -90, 'Kupić loda?', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const body = this.add
      .text(0, -30, orderSummaryPl(this.order), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6b5f7a',
        align: 'center',
        wordWrap: { width: 460 },
      })
      .setOrigin(0.5);

    const parts: Phaser.GameObjects.GameObject[] = [bg, title, body];
    const yes = UiKit.button(this, {
      x: -110,
      y: 70,
      label: `Kup · ${priceLabelPl(orderPriceZl(this.order))}`,
      width: 200,
      height: MIN_TOUCH,
      fill: 0xb8e6d0,
      onClick: () => void this.onBuyConfirmed(),
    });
    const addMore = UiKit.button(this, {
      x: 110,
      y: 70,
      label: this.order.scoops.length >= 3 ? 'Max 3 gałki' : 'Dołóż gałkę',
      width: 200,
      height: MIN_TOUCH,
      fill: 0xbde0fe,
      onClick: () => {
        this.order = closeConfirm(this.order);
        this.hideConfirmDialog();
        this.showToast(
          this.order.scoops.length >= 3
            ? 'Masz już 3 gałki!'
            : 'Kliknij smak, żeby dołożyć gałkę',
        );
      },
    });
    parts.push(yes, addMore);
    this.confirmUi = this.add.container(cx, cy, parts);
    this.confirmUi.setScrollFactor(0);
    this.confirmUi.setDepth(30_000);

    const yesHit = this.add
      .zone(cx - 110, cy + 70, 200, MIN_TOUCH)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(30_010)
      .setInteractive({ useHandCursor: true })
      .setName('hotspot-confirm-purchase')
      .on('pointerdown', () => void this.onBuyConfirmed());
    const moreHit = this.add
      .zone(cx + 110, cy + 70, 200, MIN_TOUCH)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(30_010)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        this.order = closeConfirm(this.order);
        this.hideConfirmDialog();
        this.showToast(
          this.order.scoops.length >= 3
            ? 'Masz już 3 gałki!'
            : 'Kliknij smak, żeby dołożyć gałkę',
        );
      });
    this.confirmHitZones = [yesHit, moreHit];
  }

  private hideConfirmDialog(): void {
    this.confirmHitZones.forEach((zone) => zone.destroy());
    this.confirmHitZones = [];
    this.confirmUi?.destroy(true);
    this.confirmUi = null;
  }

  private async onBuyConfirmed(): Promise<void> {
    this.hideConfirmDialog();
    this.order = closeConfirm(this.order);
    if (this.buying || this.order.busy) return;
    const flavorId = this.order.scoops[0];
    if (!flavorId) return;

    this.buying = true;
    this.order = setOrderBusy(this.order, true);
    this.showToast('Kupuję loda…');
    this.walk?.moveTo(COUNTER_APPROACH.x, COUNTER_APPROACH.y);

    // Serwer: jedna transakcja na lód (smak główny); cena lokalna = gałki
    const res = await buyIceCream(flavorId);
    this.buying = false;
    this.order = setOrderBusy(this.order, false);

    if (res.ok) {
      // Jeśli multi-scoop, odejmij lokalnie resztę (best-effort) — saldo autorytatywne z res
      this.balance = res.balance;
      balanceBus.set(this.balance);
      this.showToast(res.messagePl);
      this.setStatus('Piesek trzyma loda!');
      await this.playEatSequence();
      this.order = emptyOrderState();
      this.refreshSummary();
      return;
    }

    this.showToast(res.messagePl);
    if (res.code === 'INSUFFICIENT_FUNDS') {
      playFx(this, 'notes', COUNTER_APPROACH.x, COUNTER_APPROACH.y);
    }
  }

  private async playEatSequence(): Promise<void> {
    const scoops = [...this.order.scoops];
    const vessel = this.order.vessel;
    const pos = this.walk?.position ?? {
      x: COUNTER_APPROACH.x,
      y: COUNTER_APPROACH.y,
    };

    this.heldCone?.destroy();
    this.heldScoops = [];
    const parts: Phaser.GameObjects.GameObject[] = [];
    // naczynie
    if (vessel === 'cup') {
      if (this.textures.exists('icecream_cup_empty')) {
        parts.push(
          this.add
            .image(0, 18, 'icecream_cup_empty')
            .setDisplaySize(38, 54),
        );
      } else {
        parts.push(
          this.add
            .rectangle(0, 18, 28, 32, 0xfff8f0)
            .setStrokeStyle(3, 0xe0d4c8),
        );
      }
    } else {
      const coneKey = this.textures.exists(ICE_CREAM_CONE_TEXTURE)
        ? ICE_CREAM_CONE_TEXTURE
        : null;
      if (coneKey) {
        parts.push(
          this.add.image(0, 20, coneKey).setDisplaySize(40, 48).setOrigin(0.5, 0),
        );
      } else {
        parts.push(
          this.add.triangle(0, 28, 0, 40, -16, 0, 16, 0, 0xe8b86d),
        );
      }
    }
    // gałki jedna nad drugą
    scoops.forEach((id, i) => {
      const f = flavorById(id);
      const y = -4 - i * 18;
      if (f && this.textures.exists(f.textureKey)) {
        const scoop = this.add.image(0, y, f.textureKey).setDisplaySize(28, 28);
        parts.push(scoop);
        this.heldScoops.push(scoop);
      } else {
        const scoop = this.add.circle(0, y, 14, f?.color ?? 0xff8fab);
        parts.push(scoop);
        this.heldScoops.push(scoop);
      }
    });

    // Jedna reprezentacja zamówienia: lokalne dziecko awatara przy pyszczku.
    this.heldCone = this.add.container(30, -34, parts);
    this.heldCone.setDepth(120);
    this.avatar?.container.add(this.heldCone);
    this.registry.set('icecreamHeldScoops', scoops.length);

    playFx(this, 'hearts', pos.x, pos.y - 40);
    playFx(this, 'crumbs', pos.x, pos.y - 10);

    const avatar = this.avatar;
    if (avatar) {
      const eating = avatar.playAction('eat');
      const sequence = buildLickSequence(scoops.length);
      for (const frame of sequence.slice(1)) {
        await this.playLickContact(frame.durationMs);
        if (!this.sceneAlive) return;
        const eaten = this.heldScoops.pop();
        eaten?.destroy();
        this.registry.set('icecreamHeldScoops', frame.remainingScoops);
        playFx(this, 'crumbs', avatar.x + 24, avatar.y - 34);
      }
      await eating;
      if (!this.sceneAlive || this.avatar !== avatar) return;
      await avatar.playAction('happy');
    }

    if (this.heldCone) {
      this.tweens.add({
        targets: this.heldCone,
        alpha: 0,
        y: -78,
        duration: 500,
        onComplete: () => {
          this.heldCone?.destroy();
          this.heldCone = null;
          this.heldScoops = [];
          this.registry.set('icecreamHeldScoops', 0);
        },
      });
    }

    this.setStatus(
      scoops.length > 1
        ? `Mniam! ${scoops.length} gałki!`
        : 'Mniam! Pyszne lody!',
    );
  }

  private playLickContact(durationMs: number): Promise<void> {
    if (!this.heldCone || this.avatar?.prefersReducedMotion) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const done = () => {
        this.events.off('shutdown', done);
        resolve();
      };
      this.events.once('shutdown', done);
      this.tweens.add({
        targets: this.heldCone,
        x: 18,
        y: -28,
        scaleX: 0.9,
        scaleY: 0.9,
        duration: Math.max(80, Math.floor(durationMs / 2)),
        yoyo: true,
        onComplete: done,
      });
    });
  }

  private async refreshBalance(): Promise<void> {
    const r = await refreshIceCreamBalance();
    this.balance = r.balanceZl;
    balanceBus.set(this.balance);
  }

  private setStatus(msg: string): void {
    if (!this.statusText) return;
    this.statusText.setText(msg);
    this.statusText.setVisible(true);
  }

  private showToast(msg: string): void {
    if (!this.toast) return;
    this.toast.setText(msg);
    this.toast.setVisible(true);
    this.time.delayedCall(2400, () => this.toast?.setVisible(false));
  }

  private teardown(): void {
    this.sceneAlive = false;
    this.hideConfirmDialog();
    this.walk?.destroy();
    this.walk = null;
    this.heldCone?.destroy();
    this.heldCone = null;
    this.heldScoops = [];
    this.avatar?.destroy();
    this.avatar = null;
    this.registry.remove('icecreamHeldScoops');
    this.flavorButtons = [];
    this.selectedRing = null;
    this.registry.remove('companionFollow');
  }
}

export default IcecreamScene;
